/** @jsxImportSource @emotion/react */
import {
  Autocomplete,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material'
import { Control, Controller, FieldErrors, FieldPath, UseFormRegister } from 'react-hook-form'

import { generateS3Url } from '@app/utils/strings.utils'
import { ALLOWED_METRIC_TYPES, DataUpdateFormData, FieldConfig } from '@app/modules/Home/Data/types'
import { IconOption } from '@app/modules/Home/Data/hooks/useIconOptions'
import { formContainer } from './DataUpload.styles'

const toDisplayLabel = (name: string) =>
  name.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

const requiredLabel = (label: string) => (
  <>
    {label}
    <Box component="span" sx={{ color: 'error.main', ml: 0.25 }} aria-hidden>
      {' *'}
    </Box>
  </>
)

const TEXT_FIELDS: FieldConfig[] = [
  { name: 'name', label: 'Dataset name', required: true },
  { name: 'description', label: 'Description', textbox: true, required: true },
  { name: 'type', label: 'Type' },
  { name: 'source', label: 'Source' },
  { name: 'spatialExtent', label: 'Spatial Extent' },
  { name: 'icon', label: 'Icon' },
  { name: 'lineSegmentLength', label: 'Split Line Segment Length (m)', numeric: true },
  { name: 'polygonSegmentArea', label: 'Split Polygon Segment Area (m²)', numeric: true },
  { name: 'unit', label: 'Unit of Measurement (ha, km, buildings, etc)', required: true },
]

interface DatasetFormFieldsProps<T extends DataUpdateFormData> {
  control: Control<T>
  register: UseFormRegister<T>
  errors: FieldErrors<T>
  iconOptions: IconOption[]
  geometryType: string | undefined
  onGeometryChange: (field: { onChange: (v: string | undefined) => void }, value: string) => void
  disabled?: boolean
}

function DatasetFormFields<T extends DataUpdateFormData>({
  control,
  register,
  errors,
  iconOptions,
  geometryType,
  onGeometryChange,
  disabled = false,
}: DatasetFormFieldsProps<T>) {
  const errs = errors as FieldErrors<DataUpdateFormData>
  return (
    <Box css={formContainer}>
      {TEXT_FIELDS.map(({ name, label, textbox, numeric, required }) => {
        if (name === 'icon') {
          return (
            <Controller
              key={name}
              name={'icon' as FieldPath<T>}
              control={control}
              render={({ field }) => {
                const selected = iconOptions.find((o) => o.filename === field.value) ?? null
                return (
                  <Autocomplete
                    options={iconOptions}
                    getOptionLabel={(o) => toDisplayLabel(o.name)}
                    value={selected}
                    disabled={disabled}
                    onChange={(_, option) => field.onChange(option ? option.filename : '')}
                    renderOption={(props, option) => (
                      <Box
                        component="li"
                        {...props}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <img
                          src={generateS3Url(`s3://${option.filename}`, { preserveCase: true })}
                          width={20}
                          height={20}
                          alt=""
                        />
                        {toDisplayLabel(option.name)}
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={label}
                        error={!!errs.icon}
                        helperText={errs.icon?.message as string | undefined}
                        slotProps={{
                          input: {
                            ...params.InputProps,
                            startAdornment: field.value ? (
                              <>
                                <InputAdornment position="start" sx={{ ml: 1 }}>
                                  <img
                                    src={generateS3Url(`s3://${field.value}`, {
                                      preserveCase: true,
                                    })}
                                    width={20}
                                    height={20}
                                    alt=""
                                  />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            ) : (
                              params.InputProps.startAdornment
                            ),
                          },
                        }}
                      />
                    )}
                  />
                )
              }}
            />
          )
        }
        return (
          <TextField
            key={name}
            label={required ? requiredLabel(label) : label}
            fullWidth
            multiline={textbox}
            rows={textbox ? 3 : undefined}
            type={numeric ? 'number' : undefined}
            disabled={disabled}
            error={!!errs[name]}
            helperText={errs[name]?.message as string | undefined}
            {...register(
              name as FieldPath<T>,
              numeric
                ? { setValueAs: (v) => (v === '' || isNaN(Number(v)) ? undefined : Number(v)) }
                : {},
            )}
          />
        )
      })}

      <Controller
        name={'geometryType' as FieldPath<T>}
        control={control}
        render={({ field }) => (
          <FormControl fullWidth error={!!errs.geometryType} disabled={disabled}>
            <InputLabel>{requiredLabel('Geometry Type')}</InputLabel>
            <Select
              label="Geometry Type *"
              {...field}
              value={field.value ?? ''}
              onChange={(e) => onGeometryChange(field, e.target.value as string)}
            >
              <MenuItem value="line">Line</MenuItem>
              <MenuItem value="point">Point</MenuItem>
              <MenuItem value="polygon">Polygon</MenuItem>
            </Select>
            {errs.geometryType && (
              <FormHelperText>{errs.geometryType.message as string}</FormHelperText>
            )}
          </FormControl>
        )}
      />

      <Controller
        name={'metricType' as FieldPath<T>}
        control={control}
        render={({ field }) => {
          const allowed = geometryType
            ? ALLOWED_METRIC_TYPES[geometryType as 'line' | 'point' | 'polygon']
            : (['count', 'area', 'length'] as const)
          return (
            <FormControl fullWidth error={!!errs.metricType} disabled={disabled}>
              <InputLabel>{requiredLabel('Metric Type')}</InputLabel>
              <Select
                label="Metric Type *"
                {...field}
                value={field.value ?? ''}
                onChange={(e) => {
                  const val = e.target.value as string
                  field.onChange(val === '' ? undefined : val)
                }}
              >
                {allowed.includes('count') && <MenuItem value="count">Count</MenuItem>}
                {allowed.includes('area') && <MenuItem value="area">Area</MenuItem>}
                {allowed.includes('length') && <MenuItem value="length">Length</MenuItem>}
              </Select>
              {errs.metricType && (
                <FormHelperText>{errs.metricType.message as string}</FormHelperText>
              )}
            </FormControl>
          )
        }}
      />

      <Controller
        name={'license' as FieldPath<T>}
        control={control}
        render={({ field }) => (
          <FormControl fullWidth error={!!errs.license} disabled={disabled}>
            <InputLabel>License</InputLabel>
            <Select
              label="License"
              {...field}
              value={field.value ?? ''}
              onChange={(e) => {
                const val = e.target.value as string
                field.onChange(val === '' ? undefined : val)
              }}
            >
              <MenuItem value="Restricted">Restricted</MenuItem>
              <MenuItem value="Controlled">Controlled</MenuItem>
              <MenuItem value="Open">Open</MenuItem>
            </Select>
            {errs.license && <FormHelperText>{errs.license.message as string}</FormHelperText>}
          </FormControl>
        )}
      />

      <Controller
        name={'changeToPoint' as FieldPath<T>}
        control={control}
        render={({ field }) => (
          <FormControlLabel
            control={
              <Checkbox checked={!!field.value} onChange={field.onChange} disabled={disabled} />
            }
            label="Change to Point"
          />
        )}
      />
    </Box>
  )
}

export default DatasetFormFields
